import * as api from '@apis/admin-user';
import { ROLE_LABELS } from '@consts/role';
import { useQuery } from '@tanstack/react-query';
import { cdnImageUrl } from '@utils/image';
import { Button, Modal, Tabs } from 'antd';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React from 'react';
import { toast } from 'react-toastify';
import { UserActivityTab } from './userDetail/UserActivityTab';
import { UserInfoTab } from './userDetail/UserInfoTab';
import { UserPaymentTab } from './userDetail/UserPaymentTab';

export interface UserDetailModalProps {
  open?: boolean;
  userId?: string;
  onClose?: () => void;
}

export const UserDetailModal: React.FC<UserDetailModalProps> = (props) => {
  const userQuery = useQuery({
    queryKey: [`user-detail-${props.userId}`],
    queryFn: () => api.getUser(props.userId!),
    enabled: !!props.userId && !!props.open,
  });

  const user = userQuery.data?.data;

  const handleCopyId = () => {
    if (!props.userId) return;
    navigator.clipboard.writeText(props.userId);
    toast.dark('사용자 ID를 클립보드에 복사했습니다', {
      position: 'top-center',
      autoClose: 2000,
      hideProgressBar: true,
      theme: 'dark',
    });
  };

  return (
    <Modal className="app-modal" open={props.open} width={1200} footer={null} destroyOnClose onCancel={props.onClose}>
      <OverlayScrollbarsComponent
        defer
        options={{ scrollbars: { autoHide: 'scroll' } }}
        style={{
          backgroundColor: '#ffffff',
          marginTop: 0,
          position: 'relative',
          height: 'calc(100dvh - 200px)',
          overflow: 'auto',
        }}>
        <div style={{ padding: '8px 16px 16px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 20, fontWeight: 'bold' }}>유저 상세</div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              padding: 12,
              border: '1px solid #eaeaea',
              borderRadius: 8,
              backgroundColor: '#fafafa',
              flexWrap: 'wrap',
            }}>
            {user?.profile?.imageUrl ? (
              <img
                src={cdnImageUrl(user?.profile?.imageUrl)}
                alt="profile"
                style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: '50%',
                  backgroundColor: '#dddddd',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                }}>
                {(user?.profile?.name || user?.profile?.nickname || '?').slice(0, 1)}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                {user?.profile?.name || user?.profile?.nickname || '-'}
                {user?.profile?.nickname && user?.profile?.name ? (
                  <span style={{ fontWeight: 'normal', color: '#888', marginLeft: 6 }}>({user.profile.nickname})</span>
                ) : null}
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
                <span style={{ color: '#666' }}>{(user?.roles || []).map((r) => ROLE_LABELS[r] || r).join(' / ')}</span>
                {user?.membership?.name && (
                  <span
                    style={{
                      display: 'inline-block',
                      backgroundColor: user.membership.color || '#666',
                      color: 'white',
                      borderRadius: 4,
                      padding: '1px 6px',
                      fontSize: 11,
                    }}>
                    {user.membership.name}
                  </span>
                )}
                {user?.deletedAt && <span style={{ color: 'red', fontSize: 11, fontWeight: 'bold' }}>탈퇴</span>}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12, color: '#888' }}>
                <span>{props.userId}</span>
                <Button size="small" onClick={handleCopyId}>
                  ID 복사
                </Button>
              </div>
            </div>
          </div>

          <Tabs
            destroyInactiveTabPane
            items={[
              {
                key: 'info',
                label: '유저정보',
                children: <UserInfoTab userId={props.userId} user={user} loading={userQuery.isLoading} />,
              },
              {
                key: 'payment',
                label: '결제/포인트',
                children: <UserPaymentTab userId={props.userId} user={user} />,
              },
              {
                key: 'activity',
                label: '활동',
                children: <UserActivityTab userId={props.userId} />,
              },
            ]}
          />
        </div>
      </OverlayScrollbarsComponent>
    </Modal>
  );
};
